import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Divider,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowBack, Edit as EditIcon } from '@mui/icons-material';
import supabase from '../supabaseClient';
import { format } from 'date-fns';

const TicketContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#2b2b2b',
  color: '#fff',
  paddingTop: '2rem',
});

const TicketContent = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#1a1a1a',
  color: '#fff',
  marginBottom: '2rem',
});

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (error) throw error;
      setTicket(data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <TicketContainer>
        <Container maxWidth="lg">
          <Typography>Loading...</Typography>
        </Container>
      </TicketContainer>
    );
  }

  if (!ticket) {
    return (
      <TicketContainer>
        <Container maxWidth="lg">
          <Typography>Ticket not found</Typography>
        </Container>
      </TicketContainer>
    );
  }

  return (
    <TicketContainer>
      <Container maxWidth="lg">
        {/* Breadcrumbs Navigation */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs separator="›" sx={{ color: '#fff' }}>
            <Link
              component="button"
              onClick={() => navigate('/support')}
              sx={{ color: '#FFD700', textDecoration: 'none', cursor: 'pointer' }}
            >
              Support
            </Link>
            <Typography color="white">Ticket Details</Typography>
          </Breadcrumbs>
        </Box>

        {/* Ticket Header */}
        <TicketContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate('/support')}
                sx={{ color: '#FFD700' }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" sx={{ color: '#FFD700' }}>
                {ticket.title}
              </Typography>
            </Box>
            <Button
              startIcon={<EditIcon />}
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                '&:hover': { borderColor: '#FFD700', backgroundColor: 'rgba(255, 215, 0, 0.1)' }
              }}
              variant="outlined"
              onClick={() => navigate(`/support?edit=${ticketId}`)}
            >
              Edit Ticket
            </Button>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

          {/* Ticket Metadata */}
          <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
            <Typography sx={{ opacity: 0.8 }}>
              Status: <span style={{ color: '#FFD700' }}>{ticket.status}</span>
            </Typography>
            <Typography sx={{ opacity: 0.8 }}>
              Created: {format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm')}
            </Typography>
            <Typography sx={{ opacity: 0.8 }}>
              Department: {ticket.department}
            </Typography>
            <Typography sx={{ opacity: 0.8 }}>
              Priority: {ticket.priority}
            </Typography>
          </Box>

          {/* Ticket Description */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#FFD700' }}>
              Description
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
          </Box>

          {/* Table of Contents (if needed) */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#FFD700' }}>
              Table of Contents
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography component="div" sx={{ cursor: 'pointer', '&:hover': { color: '#FFD700' } }}>
                • Description
              </Typography>
              <Typography component="div" sx={{ cursor: 'pointer', '&:hover': { color: '#FFD700' } }}>
                • Metadata
              </Typography>
              <Typography component="div" sx={{ cursor: 'pointer', '&:hover': { color: '#FFD700' } }}>
                • Updates
              </Typography>
            </Box>
          </Box>
        </TicketContent>
      </Container>
    </TicketContainer>
  );
};

export default Ticket; 